import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { STORAGE } from "@/lib/mr";
import { BrandWordmark } from "@/components/Brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Whole-app password gate. The shared secret is verified server-side
 * (POST /api/verify-password) so the password never lives in the JS bundle.
 * On success we cache it in localStorage and forward it on every API call
 * via the X-App-Auth header (see lib/mr.js → installAxiosAuth).
 */
export default function Gate({ children }) {
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem(STORAGE.AUTH_TOKEN));
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [required, setRequired] = useState(true);
  const [capsOn, setCapsOn] = useState(false);
  const formRef = useRef(null);

  // On boot, if a token is already cached, re-verify it once silently.
  // If the server doesn't require a password at all, unlock immediately.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = localStorage.getItem(STORAGE.AUTH_TOKEN);
      try {
        const res = await axios.post(`${API}/verify-password`, { password: cached || "" }, { timeout: 12000 });
        if (cancelled) return;
        if (res.data?.required === false) {
          setRequired(false);
          localStorage.setItem(STORAGE.IS_ADMIN, "1");
          setUnlocked(true);
        } else if (cached && res.data?.ok) {
          localStorage.setItem(STORAGE.IS_ADMIN, res.data?.is_admin ? "1" : "0");
          setUnlocked(true);
        } else if (cached && !res.data?.ok) {
          // stale / rotated password
          localStorage.removeItem(STORAGE.AUTH_TOKEN);
          localStorage.removeItem(STORAGE.IS_ADMIN);
          setUnlocked(false);
        }
      } catch {
        // backend unreachable — fall back to the cached token (UI still loads)
        if (cached) setUnlocked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (unlocked) return children;

  const triggerShake = () => {
    const el = formRef.current;
    if (!el) return;
    el.classList.remove("mr-shake");
    // force reflow so the animation can replay on consecutive wrong attempts
    void el.offsetWidth;
    el.classList.add("mr-shake");
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = pw.trim();
    if (!value) return;
    setBusy(true); setErr("");
    try {
      const res = await axios.post(`${API}/verify-password`, { password: value }, { timeout: 12000 });
      if (res.data?.ok) {
        localStorage.setItem(STORAGE.AUTH_TOKEN, value);
        localStorage.setItem(STORAGE.IS_ADMIN, res.data?.is_admin ? "1" : "0");
        setUnlocked(true);
      } else {
        setErr("Wrong password");
        setPw("");
        triggerShake();
      }
    } catch {
      setErr("Could not reach the server");
      triggerShake();
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (typeof e.getModifierState === "function") {
      setCapsOn(e.getModifierState("CapsLock"));
    }
  };

  if (!required) return children;

  return (
    <div
      className="fixed inset-0 w-screen h-screen flex items-center justify-center px-6 mr-luxe mr-grain"
      style={{
        background:
          "radial-gradient(120% 80% at 50% 0%, #3a0d12 0%, #1b0508 55%, #0a0204 100%)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="gate-page"
    >
      <div className="mr-dust" aria-hidden="true" />
      <form
        ref={formRef}
        onSubmit={submit}
        className="w-full max-w-[360px] flex flex-col items-center mr-rise"
        autoComplete="off"
      >
        <BrandWordmark size="lg" aura />

        <div className="mt-8 mr-rule" style={{ width: "3.5rem" }} />

        <p className="mt-7 text-white/65 text-[12px] tracking-[0.42em] uppercase mb-4">
          Authenticate
        </p>

        <input
          type="password"
          value={pw}
          onChange={(e) => { setPw(e.target.value); setErr(""); }}
          onKeyDown={onKey}
          onKeyUp={onKey}
          placeholder="••••••••"
          autoFocus
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          className="w-full text-center bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/25 text-[16px] outline-none transition tracking-[0.4em]"
          data-testid="gate-input"
        />

        {capsOn && !err && (
          <p
            className="mt-2 text-[10px] tracking-[0.36em] uppercase"
            style={{ color: "rgba(214,201,140,0.75)" }}
            data-testid="gate-caps"
          >
            Caps lock is on
          </p>
        )}

        {err && (
          <p className="mt-3 text-[12px] tracking-[0.24em] uppercase text-red-300/90" data-testid="gate-error">
            {err}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !pw.trim()}
          className="mt-5 w-full rounded-xl py-3 text-[15px] font-medium text-[#f0ead4] disabled:opacity-40 disabled:cursor-not-allowed transition hover:brightness-110 mr-cta mr-cta-breath"
          style={{
            background:
              "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontSize: "12px",
          }}
          data-testid="gate-submit"
        >
          {busy ? "Checking…" : "Unlock"}
        </button>

        <div className="mt-10 flex flex-col items-center gap-2">
          <div className="mr-rule" style={{ width: "2.25rem" }} />
          <p
            className="text-[9px] tracking-[0.48em] uppercase"
            style={{ color: "rgba(214,201,140,0.5)" }}
            data-testid="creator-credit"
          >
            Dennis Reinsma
          </p>
        </div>
      </form>
    </div>
  );
}
